import { QUOTATION_INVOICE_DOCUMENT_TYPE_LABELS } from "@/modules/quotation-invoice/domain/document-type";
import type { DocumentsSummaryResult } from "@/modules/quotation-invoice/domain/types";
import { DollarSign, FileText, Receipt } from "lucide-react";
import { formatDocumentCurrency } from "../lib/format-document-currency";

type SummaryCardsProps = {
  data: DocumentsSummaryResult | undefined;
  isLoading?: boolean;
};

export function SummaryCards({ data, isLoading }: SummaryCardsProps) {
  if (isLoading) {
    return (
      <div className="grid gap-4 md:grid-cols-3">
        {[1, 2, 3].map((i) => (
          <div key={i} className="animate-pulse rounded-xl border bg-card p-6">
            <div className="h-4 w-40 rounded bg-muted" />
            <div className="mt-4 space-y-3">
              <div className="h-6 w-full rounded bg-muted" />
              <div className="h-6 w-full rounded bg-muted" />
              <div className="h-6 w-full rounded bg-muted" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  const blocks = data?.byCurrency ?? [];

  return (
    <div className="grid gap-4 md:grid-cols-3">
      <div className="rounded-xl border bg-card p-6">
        <div className="flex items-center gap-2 text-muted-foreground">
          <DollarSign className="h-4 w-4 shrink-0 text-blue-500" />
          <span className="font-medium text-sm">
            {QUOTATION_INVOICE_DOCUMENT_TYPE_LABELS.quotation}
          </span>
        </div>
        <p className="mt-1 text-muted-foreground text-xs leading-snug">
          ຍອດຂໍ້ສະເໜີລວມ — ອອກແລ້ວ / ມີການຊຳລະບາງສ່ວນ
        </p>
        <ul className="mt-4 space-y-3">
          {blocks.map((b) => (
            <li
              key={b.currency}
              className="border-border/60 border-b pb-3 last:border-0 last:pb-0"
            >
              <div className="flex items-start justify-between gap-2">
                <span className="font-medium text-muted-foreground text-xs tracking-wide">
                  {b.currency}
                </span>
                <div className="text-right">
                  <p className="font-bold text-lg tabular-nums leading-tight">
                    {formatDocumentCurrency(
                      b.totalQuotationsAmount,
                      b.currency,
                    )}
                  </p>
                  <p className="text-muted-foreground text-xs">
                    {b.totalQuotationsCount} ລາຍການ
                  </p>
                </div>
              </div>
            </li>
          ))}
        </ul>
      </div>

      <div className="rounded-xl border bg-card p-6">
        <div className="flex items-center gap-2 text-muted-foreground">
          <FileText className="h-4 w-4 shrink-0 text-amber-500" />
          <span className="font-medium text-sm">
            {QUOTATION_INVOICE_DOCUMENT_TYPE_LABELS.invoice} — ຄ້າງຊຳລະ
          </span>
        </div>
        <p className="mt-1 text-muted-foreground text-xs leading-snug">
          ຍອດຄ້າງຮັບຕາມງວດ — ຫຼາຍໃບຕໍ່ 1 Quotation ໄດ້; ບໍ່ລວມໃບປິດຊຳລະແລ້ວ
        </p>
        <ul className="mt-4 space-y-3">
          {blocks.map((b) => (
            <li
              key={b.currency}
              className="border-border/60 border-b pb-3 last:border-0 last:pb-0"
            >
              <div className="flex items-start justify-between gap-2">
                <span className="font-medium text-muted-foreground text-xs tracking-wide">
                  {b.currency}
                </span>
                <div className="text-right">
                  <p className="font-bold text-lg tabular-nums leading-tight">
                    {formatDocumentCurrency(
                      b.pendingInvoicesOutstandingAmount,
                      b.currency,
                    )}
                  </p>
                  <p className="text-muted-foreground text-xs">
                    {b.pendingInvoicesCount} ໃບ
                  </p>
                </div>
              </div>
            </li>
          ))}
        </ul>
      </div>

      <div className="rounded-xl border bg-card p-6">
        <div className="flex items-center gap-2 text-muted-foreground">
          <Receipt className="h-4 w-4 shrink-0 text-emerald-500" />
          <span className="font-medium text-sm">
            {QUOTATION_INVOICE_DOCUMENT_TYPE_LABELS.receipt}
          </span>
        </div>
        <p className="mt-1 text-muted-foreground text-xs leading-snug">
          ຍອດຕາມໃບຮັບທັງໝົດ — ບໍ່ກອງຕາມສະຖານະ
        </p>
        <ul className="mt-4 space-y-3">
          {blocks.map((b) => (
            <li
              key={b.currency}
              className="border-border/60 border-b pb-3 last:border-0 last:pb-0"
            >
              <div className="flex items-start justify-between gap-2">
                <span className="font-medium text-muted-foreground text-xs tracking-wide">
                  {b.currency}
                </span>
                <div className="text-right">
                  <p className="font-bold text-lg tabular-nums leading-tight">
                    {formatDocumentCurrency(b.paidReceiptsAmount, b.currency)}
                  </p>
                  <p className="text-muted-foreground text-xs">
                    {b.paidReceiptsCount} ລາຍການ
                  </p>
                </div>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
